import React, { useState, useEffect } from "react";
import { Table, Button, ToastContainer, Toast } from "react-bootstrap";
import { FaTrash, FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getSaveSearchDetails,
  updateSaveSearchName,
  getSavedSearchAndPropertyCount,
  getUserSavedSearch,
  DeleteUserSavedSearch,
} from "@/api/savedSearch";
import ConfirmationModel from "@/components/common/ConfirmationModel/ConfirmationModel";
import DashboardSectionWrapper from "../components/DashboardSectionWrapper";
import { ShimmerTable } from "react-shimmer-effects";
import { useNavigate } from "react-router";
import { formUrl } from "@/components/srp/searchParams";
import CustomModel from "@/components/common/ConfirmationModel/CustomModal";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";
import { markInternalNavigation } from "@/utils/attributionCapture";

const SavedSearches = () => {
  const navigate = useNavigate();
  const [savedSearchesList, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavedSearchLoading, setIsSavedSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowID, setSelectedRowID] = useState(null);
  // State to track which row is being edited
  const [editingId, setEditingId] = useState(null);
  // State to store the temporary name while editing
  const [editName, setEditName] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [messageToShow, setMessageToShow] = useState(null);
  const [deleteStatus, setdeleteStatus] = useState(false);

  useEffect(() => {
    const emailId = "";
    getSavedSearchAndPropertyCount(emailId);
    fetchSavedSearches(0);
  }, []);

  // for pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const handleEditClick = (searchId, currentName) => {
    setIsEditing(true);
    setEditingId(searchId);
    setEditName(currentName); // Set the current name as initial value
  };

  const handleNameChange = (e) => {
    setEditName(e.target.value);
  };

  const handleUpdateName = async (currentRowData) => {
    if (!editName.trim()) return;
    try {
      const payload = {
        searchId: currentRowData.saveSearchId,
        searchName: editName.trim(),
      };
      await updateSaveSearchName(payload);
      // Update local state
      toast.success("Search Name Updated Successfully!", {
        position: "top-center",
      });
      setSavedSearches((prevSearches) =>
        prevSearches.map((search) =>
          search.id === search.saveSearchId
            ? { ...search, searchName: editName.trim() }
            : search
        )
      );

      trackApiCall({
        eventName: "edit_save_search_success",
        eventCategory: "Saved-Searches",
        eventSource: "Edit saved search success",
      });

      // fetchUserSavedSearches();
      fetchSavedSearches(0, true);

      // Reset edit state
      setEditingId(null);
      setEditName("");
    } catch (error) {
      console.error("Error updating search name:", error);
      trackApiCall({
        eventName: "edit_save_search_error",
        eventCategory: "Saved-Searches",
        eventSource: "Edit saved search error",
      });
      // You could add error notification here
    }
  };

  const criteriaLabelMap = {
    PRICE_RANGE: "Price",
    SQFT_RANGE: "Sq. Feet",
    YEAR_BUILT: "Year Built",
    auctionEndingToday: "Listing Status",
    bankOwned: "Bank Owned",
    bath: "Bathroom",
    bed: "Bedroom",
    brokerCoOp: "Featured",
    cashOnly: "Featured",
    category: "Listing Type",
    listingType: "Sales Type",
    newToHubzu: "Listing Status",
    noBuyerPremiumFilter: "Featured",
    nonBankOwned: "Non-Bank Owned",
    nonOccupied: "Non Occupied",
    npl: "Non Performing Loan",
    occupancyStatus: "Occupancy Status",
    occupied: "Occupied",
    orderBy: "Order By",
    ownItNow: "End the Auction Now",
    previewProperty: "Listing Type",
    propertyShowMoreStatus: "Show Only",
    propertyStatus: "Listing Status",
    propertySubType: "Property Type",
    reservepricerevealed: "Featured",
    selfshowingenabled: "Featured",
    sortBy: "Sort By",
    sortOrder: "Sort Order",
  };
  // for drawer flyout menu
  const fetchUserSavedSearches = async () => {
    try {
      const emailId = "";
      const pageNumber = 0;
      const pageSize = "3";

      setLoading(true);
      await getUserSavedSearch(emailId, pageNumber, pageSize);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching saved searches:", err);
    } finally {
      setLoading(false);
    }
  };

  const rangeLabels = {
    PRICE_RANGE: {
      above: "Above",
      below: "Below",
    },
    YEAR_BUILT: {
      before: "Before",
      after: "After",
    },
    SQFT_RANGE: {
      greaterThan: "Greater than",
      lessThan: "Less than",
    },
  };
  const getRange = (minVal, maxVal, identifier) => {
    let range = "";

    if (minVal && maxVal) {
      range = `${minVal} - ${maxVal}`;
    } else if (!minVal) {
      const label = rangeLabels[identifier]?.below || "Below";
      range = `${label} ${maxVal}`;
    } else {
      const label = rangeLabels[identifier]?.above || "Above";
      range = `${label} ${minVal}`;
    }

    return {
      criteriaKey: identifier,
      criteriaValue: range,
    };
  };
  const formatSearchCriteriaList = (criteriaList = []) => {
    let minPrice = "",
      maxPrice = "",
      minSqrFt = "",
      maxSqrFt = "";
    let minYearBuilt = "",
      maxYearBuilt = "";

    const groupedCriteria = new Map();

    const addToGroup = (label, value, key) => {
      if (!groupedCriteria.has(label)) {
        groupedCriteria.set(label, {
          criteriaKey: key,
          criteriaLabel: label,
          values: [],
        });
      }
      groupedCriteria.get(label).values.push(value);
    };

    criteriaList.forEach((criteria) => {
      const { criteriaKey, criteriaValue, criteriaValueDescription } = criteria;
      const label = criteriaLabelMap[criteriaKey] || criteriaKey;

      switch (criteriaKey) {
        case "minPriceValue":
          minPrice = criteriaValue;
          break;

        case "maxPriceValue":
          maxPrice = criteriaValue;
          break;

        case "minPropertySize":
          minSqrFt = criteriaValue;
          break;

        case "maxPropertySize":
          maxSqrFt = criteriaValue;
          break;

        case "minYearBuilt":
          minYearBuilt = criteriaValue;
          break;

        case "maxYearBuilt":
          maxYearBuilt = criteriaValue;
          break;

        case "bath":
        case "bed":
          addToGroup(label, `${criteriaValue}+`, criteriaKey);
          break;

        case "previewProperty":
        case "noBuyerPremiumFilter":
        case "reservepricerevealed":
        case "selfshowingenabled":
        case "brokerCoOp":
        case "cashOnly":
          addToGroup(label, criteriaValueDescription, criteriaKey);
          break;

        case "newToHubzu":
        case "auctionEndingToday":
          addToGroup(
            criteriaLabelMap["propertyStatus"],
            criteriaValueDescription,
            "propertyStatus"
          );
          break;

        case "occupancyStatus":
          const occupancyVal = criteriaValue
            .split(",")
            .map((val) => (val === "Y" ? "Occupied" : "Non Occupied"));
          addToGroup(label, occupancyVal.join(", "), criteriaKey);
          break;

        default:
          if (
            ![
              "rectSrchFlag",
              "neLatLng",
              "swLatLng",
              "centerLatLng",
              "zoomValue",
              "view",
              "latitude",
              "longitude",
            ].includes(criteriaKey)
          ) {
            addToGroup(
              label,
              criteriaValueDescription || criteriaValue,
              criteriaKey
            );
          }
      }
    });

    // Add derived range fields
    if (minPrice || maxPrice) {
      const range = getRange(minPrice || "$0", maxPrice, "PRICE_RANGE");
      addToGroup(
        criteriaLabelMap["PRICE_RANGE"],
        range.criteriaValue,
        "PRICE_RANGE"
      );
    }

    if (minYearBuilt || maxYearBuilt) {
      const yearRange = getRange(
        minYearBuilt || "Before 1800",
        maxYearBuilt || "After 2010",
        "YEAR_BUILT"
      );
      addToGroup(
        criteriaLabelMap["YEAR_BUILT"],
        yearRange.criteriaValue,
        "YEAR_BUILT"
      );
    }

    if (minSqrFt || maxSqrFt) {
      const sqftRange = getRange(
        minSqrFt || dUtil.formatSqFt("0"),
        maxSqrFt,
        "SQFT_RANGE"
      );
      addToGroup(
        criteriaLabelMap["SQFT_RANGE"],
        sqftRange.criteriaValue,
        "SQFT_RANGE"
      );
    }

    // Convert grouped map to array
    const updatedCriteria = Array.from(groupedCriteria.values()).map(
      (item) => ({
        criteriaKey: item.criteriaKey,
        criteriaLabel: item.criteriaLabel,
        criteriaValue: item.values.join(", "),
      })
    );

    return updatedCriteria;
  };

  const fetchSavedSearches = async (page = 0, reset = false) => {
    page > 0 &&
      trackButtonClick({
        eventName: "load_more_click",
        eventCategory: "Saved-Searches",
        eventSource: "Load more click",
        eventLabel: `Load more click - page number - ${page + 1}`,
      });
    try {
      !isEditing && setIsSavedSearchLoading(true);
      setIsLoading(true);

      const response = await getSaveSearchDetails(page);
      const rawList = response.saveSearchResponseList || [];

      // Set total count from API response
      if (page === 0 && response.saveSearchCount) {
        setTotalCount(response.saveSearchCount);
      }

      const formattedList = rawList.map((item) => {
        // Format the search criteria first
        const searchCriteriaListFormatted = formatSearchCriteriaList(
          item.searchCriteriaList || []
        );

        return {
          ...item,
          searchCriteriaListFormatted,
          formattedUrl: formUrl(item), // Add the generated form URL using the formatted criteria to each item
        };
      });

      if (reset) {
        setSavedSearches(formattedList); // Replace list
        setCurrentPage(0);
        setHasMore(response.saveSearchCount > 8);
      } else {
        setSavedSearches((prev) => [...prev, ...formattedList]); // Append
        setCurrentPage(page);
        if ((page + 1) * 8 >= response.saveSearchCount) {
          setHasMore(false);
        }
      }
      page > 0 &&
        trackApiCall({
          eventName: "load_more_success",
          eventCategory: "Saved-Searches",
          eventSource: "Load more success",
          eventLabel: `Load more success - page number - ${page + 1}`,
        });
    } catch (err) {
      setError(err.message);
      console.error("Error fetching saved searches:", err);
    } finally {
      setIsSavedSearchLoading(false);
      setIsLoading(false);
    }
  };

  const handleDelete = async (searchId) => {
    setShowConfirmation(true);
    setSelectedRowID(searchId);

    trackButtonClick({
      eventName: "delete_image_click",
      eventCategory: "Saved-Searches",
      eventSource: "Delete saved search click",
    });
  };

  const handleDeleteSearch = async () => {
    setShowConfirmation(false);
    trackButtonClick({
      eventName: "delete_conf_yes_click",
      eventCategory: "Saved-Searches",
      eventSource: "Delete saved search modal yes click success",
      eventLabel: selectedRowID,
    });

    try {
      const response = await DeleteUserSavedSearch(selectedRowID);
      toast.success("Save Search Deleted Successfully!", {
        position: "top-center",
      });
      trackApiCall({
        eventName: "delete_success",
        eventCategory: "Saved-Searches",
        eventSource: "Delete saved search modal yes click success",
        eventLabel: selectedRowID,
      });
      // Update local state to remove the deleted search
      setSavedSearches((prevSearches) =>
        prevSearches.filter((search) => search.id !== selectedRowID)
      );
      fetchSavedSearches(0, true);
    } catch (error) {
      console.error("Error deleting search:", error);
      trackApiCall({
        eventName: "delete_failure",
        eventCategory: "Saved-Searches",
        eventSource: "Delete saved search modal yes click failure",
      });
    }
  };

  const onCloseConfirmationModal = () => {
    setShowConfirmation(false);

    trackButtonClick({
      eventName: "delete_conf_no_click",
      eventCategory: "Saved-Searches",
      eventSource: "Delete saved search modal no click",
    });
  };

  return (
    <DashboardSectionWrapper title="Saved Searches">
      <div className="">
        {isSavedSearchLoading ? (
          <div className="p-4 w-100">
            <ShimmerTable row={3} col={4} />
          </div>
        ) : savedSearchesList.length > 0 ? (
          <Table className="mt-2 savedSearch-table" responsive>
            <thead>
              <tr>
                <th>Search Name</th>
                <th>Search Criteria</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {savedSearchesList.map((item, index) => (
                <tr key={index}>
                  <td>
                    {editingId === item.saveSearchId ? (
                      <div className="inline-edit-name">
                        <input
                          type="text"
                          value={editName}
                          onChange={handleNameChange}
                          //autoFocus
                          className="form-control"
                          style={{
                            width: "200px",
                            height: "40px",
                            "font-size": "12px",
                          }}
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label="Edit"
                          style={{ color: "white" }}
                          onClick={() => handleUpdateName(item)}
                        >
                          <FaCheck />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          aria-label="save"
                          style={{ color: "#333" }}
                          onClick={() => setEditingId(null)}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          "margin-top": "8px",
                          width: "300px",
                        }}
                      >
                        <a
                          href={item.formattedUrl || "#"}
                          style={{
                            color: "#2A6496",
                            fontFamily: "robotoregular",
                            fontSize: "12.04px",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            markInternalNavigation();
                            trackButtonClick({
                              eventName: "search_link_click",
                              eventCategory: "Saved-Searches",
                              eventSource: "On Saved Search link click",
                            });
                          }}
                          className="hover-underline"
                          onMouseOver={(e) =>
                            (e.target.style.textDecoration = "underline")
                          }
                          onMouseOut={(e) =>
                            (e.target.style.textDecoration = "none")
                          }
                        >
                          {item.searchName}
                        </a>

                        <button
                          onClick={() =>
                            handleEditClick(item.saveSearchId, item.searchName)
                          }
                          className="btn btn-link p-0"
                          aria-label="Edit-click"
                          style={{ color: "inherit" }}
                        >
                          <FaEdit />
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ "margin-top": "12px" }}>
                      <div className="search-criteria">
                        <div className="search-criteria-lable">Search By: </div>
                        <div
                          style={{
                            color: "#787878",
                            fontFamily: "robotoregular",
                            fontSize: "12.04px",
                          }}
                        >
                          {item?.searchBy}
                        </div>
                      </div>

                      {/* Render search criteria key-values */}

                      {Array.isArray(item?.searchCriteriaListFormatted) &&
                        item.searchCriteriaListFormatted.length > 0 && (
                          <>
                            {item.searchCriteriaListFormatted
                              .slice(0, 2)
                              .map((criteria, index) => (
                                <div className="search-criteria" key={index}>
                                  <div className="search-criteria-lable">
                                    {criteria.criteriaLabel}:{" "}
                                  </div>
                                  <div
                                    style={{
                                      color: "#787878",
                                      fontFamily: "robotoregular",
                                      fontSize: "12.04px",
                                    }}
                                  >
                                    {criteria.criteriaValue}
                                  </div>
                                </div>
                              ))}
                            {item.searchCriteriaListFormatted.length > 2 && (
                              <div
                                className="view-more-label"
                                style={{ color: "#007bff", cursor: "pointer" }}
                              >
                                <span
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowMore(true);
                                  }}
                                  style={{
                                    color: "#1D83BE",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  View more
                                </span>
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  </td>
                  {/* <td>
                     {item?.searchCriteriaList[0]?.criteriaValueDescription !==
                     ""
                       ? item?.searchCriteriaList[0]?.criteriaValueDescription
                       : item?.searchCriteriaList[0]?.criteriaValue}
                   </td> */}
                  <td>
                    <Button
                      variant="light"
                      style={{ color: "grey" }}
                      aria-label="delete"
                      onClick={() => handleDelete(item.saveSearchId)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div
            className="text-center details mt-4"
            style={{ color: "#333", fontFamily: "robotolight" }}
          >
            Save your searches so we can tell you about new listings that match
            what you're looking for.
          </div>
        )}
        <div className="confirmationModel">
          <ConfirmationModel
            show={showConfirmation}
            onHide={onCloseConfirmationModal}
            title="Confirm Delete"
            content="Are you sure you want to delete?"
            onYes={handleDeleteSearch}
            onNo={onCloseConfirmationModal}
            loading={loading}
            yesButtonText="Yes"
            noButtonText="No"
          />
        </div>

        {showMore && selectedItem && (
          <div className="customModel">
            <CustomModel
              show={showMore}
              onHide={() => setShowMore(false)}
              title="Search Criteria"
              content={selectedItem}
              loading={loading}
            />
          </div>
        )}

        {hasMore && (
          <div className="mb-20 text-center">
            <button
              className="btn btn-primary btn-sm"
              aria-label="show-more"
              onClick={() => fetchSavedSearches(currentPage + 1)}
              disabled={isLoading}
              style={{ marginTop: "1rem" }}
            >
              {isLoading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </DashboardSectionWrapper>
  );
};

export default SavedSearches;
